import Input from '../../../Utility/FormElements/Input';
import Select from '../../../Utility/FormElements/Select';
import { StoreEnvironment, StoreI18n, StoreSettings } from '../../../../Types/StoreTypes';
import { EmbeddingModel, LoggerSettings, Model } from '../../../../Types';
import { Button } from '../../../Utility/Button';
import TextareaAutosize from 'react-textarea-autosize';
import {
    MDXEditor,
    headingsPlugin,
    toolbarPlugin,
    linkPlugin,
    BoldItalicUnderlineToggles,
    BlockTypeSelect,
    ListsToggle,
} from '@mdxeditor/editor';
interface GeneralSettingsProps {
    SettingsActions: any;
    i18n: StoreI18n['i18n'];
    windowWidth: StoreEnvironment['windowWidth'];
    getBreakpoint: (breakpoint: string) => number;
    models: Partial<Model>[];
    embeddingModels: Partial<EmbeddingModel>[];
    mdParser: any;
    Currentsettings: StoreSettings;
}

function GeneralSettings({
    models,
    embeddingModels,
    mdParser,
    getBreakpoint,
    windowWidth,
    SettingsActions,
    i18n,
    Currentsettings,
}: GeneralSettingsProps) {
    return (
        <div className="w-full flex gap-8 flex-col">
            <h4 className="block">{i18n.translate({ id: 'general', sourceName: 'Settings' })}</h4>
            <label htmlFor="settings_maxCosts_input" className="flex flex-col gap-2">
                {i18n.translate({ id: 'title.costs', sourceName: 'Settings' })}
                <Input
                    inputType="number"
                    value={Currentsettings.maxCosts}
                    onChange={(maxCosts) => SettingsActions.updateCurrentSettings({ maxCosts: maxCosts })}
                    inputProps={{ min: 0, step: 0.1 }}
                    id={'settings_maxCosts_input'}
                />
            </label>
            <label htmlFor="proxyUrlHttps" className="flex flex-col gap-2">
                {i18n.translate({
                    id: 'proxyUrlHttps',
                    sourceName: 'Settings',
                })}
                <Input
                    inputType="string"
                    value={Currentsettings.proxyUrlHttps}
                    onChange={(proxyUrlHttps) =>
                        SettingsActions.updateCurrentSettings({ proxyUrlHttps: proxyUrlHttps })
                    }
                    id={'proxyUrlHttps'}
                />
            </label>
            <label htmlFor="proxyExceptions" className="flex flex-col gap-2">
                {i18n.translate({
                    id: 'proxyExceptions',
                    sourceName: 'Settings',
                })}
                <Input
                    inputType="string"
                    value={Currentsettings.proxyExceptions}
                    onChange={(proxyExceptions) =>
                        SettingsActions.updateCurrentSettings({ proxyExceptions: proxyExceptions })
                    }
                    id={'proxyExceptions'}
                    placeholder={i18n.translate({ id: 'proxyExceptions.placeholder', sourceName: 'Settings' })}
                />
            </label>
            <label htmlFor="proxyCertificatePem" className="flex flex-col gap-2">
                {i18n.translate({
                    id: 'proxyCertificatePem',
                    sourceName: 'Settings',
                })}
                <TextareaAutosize
                    id="proxyCertificatePem"
                    className="bg-input-backgroundColor w-full p-3 border border-2 border-input-borderColor rounded-lg outline-none focus:border-primary transition-all duration-100"
                    value={Currentsettings.proxyCertificatePem}
                    onChange={(e) => SettingsActions.updateCurrentSettings({ proxyCertificatePem: e.target.value })}
                    placeholder={i18n.translate({
                        id: 'proxyCertificatePem.placeholder',
                        sourceName: 'Settings',
                    })}
                />
            </label>
            <label htmlFor="settings_summaryModel_input" className="flex flex-col gap-2">
                {i18n.translate({
                    id: 'summaryModel',
                    sourceName: 'Settings',
                })}

                <Select
                    value={Currentsettings.summaryModel}
                    onChange={(summaryModel) => SettingsActions.updateCurrentSettings({ summaryModel: summaryModel })}
                    id={'settings_summaryModel_input'}
                >
                    {models
                        .filter((model: Partial<Model>) => model !== undefined)
                        .map((model: Partial<Model>) => (
                            <option key={model!.identifier ?? ''} value={model!.identifier ?? ''}>
                                {model!.title ?? ''}
                            </option>
                        ))}
                </Select>
            </label>

            <label htmlFor="settings_defaultModel_input" className="flex flex-col gap-2">
                {i18n.translate({
                    id: 'defaultModel',
                    sourceName: 'Settings',
                })}

                <Select
                    value={Currentsettings.defaultModel}
                    onChange={(defaultModel) => SettingsActions.updateCurrentSettings({ defaultModel: defaultModel })}
                    id={'settings_defaultModel_input'}
                >
                    {models
                        .filter((model: Partial<Model>) => model !== undefined)
                        .map((model: Partial<Model>) => (
                            <option key={model!.identifier ?? ''} value={model!.identifier ?? ''}>
                                {model!.title ?? ''}
                            </option>
                        ))}
                </Select>
            </label>

            <label htmlFor="settings_embeddingModel_input" className="flex flex-col gap-2">
                {i18n.translate({
                    id: 'embeddingModel',
                    sourceName: 'Settings',
                })}

                <Select
                    value={Currentsettings.embeddingModel}
                    onChange={(embeddingModel) =>
                        SettingsActions.updateCurrentSettings({ embeddingModel: embeddingModel })
                    }
                    id={'settings_embeddingModel_input'}
                >
                    {embeddingModels.map((embeddingModel: Partial<EmbeddingModel>) => (
                        <option key={embeddingModel!.identifier ?? ''} value={embeddingModel!.identifier ?? ''}>
                            {embeddingModel!.title ?? ''}
                        </option>
                    ))}
                </Select>
            </label>

            <label htmlFor="settings_chatDuration_input" className="flex flex-col gap-2">
                {i18n.translate({
                    id: 'chatDuration',
                    sourceName: 'Settings',
                })}
                <div className="flex gap-2 justify-between">
                    <div className={'min-w-min'}>
                        <Select
                            value={
                                Currentsettings.chatDuration.includes(':')
                                    ? Currentsettings.chatDuration.split(':')[0] + ':'
                                    : Currentsettings.chatDuration
                            }
                            onChange={(chatDuration) =>
                                SettingsActions.updateCurrentSettings({ chatDuration: chatDuration })
                            }
                            id={'settings_chatDurationType_input'}
                        >
                            {[
                                {
                                    text: i18n.translate({ id: 'chatDuration.infinite', sourceName: 'Settings' }),
                                    value: 'infinite',
                                },
                                {
                                    text: i18n.translate({ id: 'chatDuration.after', sourceName: 'Settings' }),
                                    value: 'after:',
                                },
                                {
                                    text: i18n.translate({ id: 'chatDuration.notBefore', sourceName: 'Settings' }),
                                    value: 'not-before:',
                                },
                            ].map((type) => (
                                <option key={type.value} value={type.value}>
                                    {type.text}
                                </option>
                            ))}
                        </Select>
                    </div>
                    <div className={'w-full'}>
                        <Input
                            optionalText={
                                Currentsettings.chatDuration === 'infinite'
                                    ? undefined
                                    : i18n.translate({
                                          id:
                                              'chatDuration.' +
                                              Currentsettings.chatDuration
                                                  .replace('not-before', 'notBefore')
                                                  .split(':')[0] +
                                              '.hint',
                                          sourceName: 'Settings',
                                      })
                            }
                            placeholder={i18n.translate({ id: 'chatDuration.days', sourceName: 'Settings' })}
                            disabled={Currentsettings.chatDuration === 'infinite'}
                            inputType="number"
                            value={Currentsettings.chatDuration.split(':')[1] ?? ''}
                            onChange={(chatDuration) => {
                                SettingsActions.updateCurrentSettings({
                                    chatDuration:
                                        Currentsettings.chatDuration.split(':')[0] +
                                        ':' +
                                        (isNaN(parseInt(chatDuration)) ? '' : chatDuration),
                                });
                            }}
                            inputProps={{ min: 1, step: 1 }}
                            id={'settings_chatDuration_input'}
                        />
                    </div>
                </div>
            </label>

            {(['audit', 'auth', 'dlp', 'connector', 'scim'] as (keyof LoggerSettings)[]).map((logType) => (
                <label htmlFor={`settings_${logType}LogFileDuration_input`} className="flex flex-col gap-2">
                    {i18n
                        .translate({
                            id: logType === 'scim' ? 'maintenance.logFileDuration' : 'compliance.logFileDuration',
                            sourceName: 'Settings',
                        })
                        .replace(
                            '{logType}',
                            { audit: 'Audit', auth: 'Auth', dlp: 'DLP', connector: 'Connector', scim: 'SCIM' }[logType],
                        )}
                    <Input
                        inputType="number"
                        value={Currentsettings.loggerSettings[logType]?.retentionPeriod ?? 30}
                        onChange={(logFileDuration) =>
                            SettingsActions.updateCurrentSettings({
                                loggerSettings: {
                                    ...Currentsettings.loggerSettings,
                                    [logType]: {
                                        ...(Currentsettings.loggerSettings[logType] ?? {}),
                                        retentionPeriod: parseInt(logFileDuration),
                                    },
                                },
                            })
                        }
                        inputProps={{ min: 1, step: 1 }}
                        id={`settings_${logType}LogFileDuration_input`}
                    />
                </label>
            ))}

            <label htmlFor="knowledgebaseSearchMode" className="flex flex-col gap-2">
                {i18n.translate({
                    id: 'knowledgebaseSearchMode',
                    sourceName: 'Settings',
                })}
                <Select
                    value={Currentsettings.knowledgebaseSearchMode || 'hybrid'}
                    onChange={(knowledgebaseSearchMode) =>
                        SettingsActions.updateCurrentSettings({ knowledgebaseSearchMode: knowledgebaseSearchMode })
                    }
                    id="knowledgebaseSearchMode"
                >
                    <option value="hybrid">
                        {i18n.translate({
                            id: 'knowledgebaseSearchMode.hybrid',
                            sourceName: 'Settings',
                        })}
                    </option>
                    <option value="similarity">
                        {i18n.translate({
                            id: 'knowledgebaseSearchMode.similarity',
                            sourceName: 'Settings',
                        })}
                    </option>
                </Select>
            </label>

            <div>
                <label className="flex flex-col gap-2 mb-2">
                    {i18n.translate({
                        id: 'title.protectionText',
                        sourceName: 'Settings',
                    })}
                </label>
                <MDXEditor
                    contentEditableClassName="w-full bg-layout-bodyBackground !text-layout-textcolor p-4 outline-none min-h-[350px]"
                    className="w-full rounded border border-input-borderColor
                    [&_.mdxeditor-toolbar]:bg-input-backgroundColor
                    [&_.mdxeditor-toolbar]:p-4
                    [&_.mdxeditor-toolbar]:text-layout-textcolor
                    [&_.mdxeditor-toolbar_button]:bg-input-backgroundColor
                    [&_.mdxeditor-toolbar_button:hover]:bg-primary
                    [&_.mdxeditor-toolbar_button:hover]:text-layout-textColorOnPrimary
                    [&_.mdxeditor-toolbar_button:hover_svg]:text-layout-textColorOnPrimary
                    [&_.mdxeditor-toolbar_button[data-state='on']]:bg-primary
                    [&_.mdxeditor-toolbar_button[data-state='on']_svg]:text-layout-textColorOnPrimary
                    [&_.mdxeditor-toolbar_button_svg]:text-layout-textcolor
                    [&_.mdxeditor-select-content]:border
                    [&_.mdxeditor-select-content]:border-layout-borderColor
                    [&_.mdxeditor-select-content_div_[role='option']]:bg-layout-bodyBackground
                    [&_.mdxeditor-select-content_div_[role='option']:hover]:bg-input-backgroundHoverColor
                    [&_.mdxeditor-select-content_div]:text-layout-textcolor
                    [&_button[role='combobox']]:bg-input-backgroundColor
                    [&_button[role='combobox']:hover]:bg-input-backgroundHoverColor
                    [&_button[role='combobox']:hover]:text-layout-textcolor
                    [&_button[role='combobox']:hover_svg]:text-layout-textcolor
                    "
                    markdown={Currentsettings.dataProtectionText}
                    onChange={(markdown) =>
                        SettingsActions.updateCurrentSettings({
                            dataProtectionText: markdown,
                        })
                    }
                    translation={(key, defaultValue, interpolations) => {
                        const translations: Record<string, string> = {
                            'toolbar.bold': i18n.translate({ id: 'mdxEditor.bold', sourceName: 'Settings' }),
                            'toolbar.removeBold': i18n.translate({
                                id: 'mdxEditor.removeBold',
                                sourceName: 'Settings',
                            }),
                            'toolbar.italic': i18n.translate({ id: 'mdxEditor.italic', sourceName: 'Settings' }),
                            'toolbar.removeItalic': i18n.translate({
                                id: 'mdxEditor.removeItalic',
                                sourceName: 'Settings',
                            }),
                            'toolbar.underline': i18n.translate({ id: 'mdxEditor.underline', sourceName: 'Settings' }),
                            'toolbar.removeUnderline': i18n.translate({
                                id: 'mdxEditor.removeUnderline',
                                sourceName: 'Settings',
                            }),
                            'toolbar.bulletedList': i18n.translate({
                                id: 'mdxEditor.bulletedList',
                                sourceName: 'Settings',
                            }),
                            'toolbar.numberedList': i18n.translate({
                                id: 'mdxEditor.numberedList',
                                sourceName: 'Settings',
                            }),
                            'toolbar.blockTypeSelect.selectBlockTypeTooltip': i18n.translate({
                                id: 'mdxEditor.selectBlockType',
                                sourceName: 'Settings',
                            }),
                            'toolbar.blockTypeSelect.placeholder': i18n.translate({
                                id: 'mdxEditor.blockType',
                                sourceName: 'Settings',
                            }),
                            'toolbar.blockTypes.paragraph': i18n.translate({
                                id: 'mdxEditor.paragraph',
                                sourceName: 'Settings',
                            }),
                            'toolbar.blockTypes.quote': i18n.translate({
                                id: 'mdxEditor.quote',
                                sourceName: 'Settings',
                            }),
                            'toolbar.blockTypes.heading': i18n.translate({
                                id: 'mdxEditor.heading',
                                sourceName: 'Settings',
                            }),
                        };
                        let result = translations[key] || defaultValue;
                        if (interpolations) {
                            Object.entries(interpolations).forEach(([k, v]) => {
                                result = result.replace(`{{${k}}}`, String(v));
                            });
                        }
                        return result;
                    }}
                    plugins={[
                        headingsPlugin(),
                        linkPlugin(),
                        toolbarPlugin({
                            toolbarContents: () => (
                                <>
                                    <BoldItalicUnderlineToggles />
                                    <BlockTypeSelect />
                                    <ListsToggle options={['bullet', 'number']} />
                                </>
                            ),
                        }),
                    ]}
                />
            </div>

            <div className="mt-5 text-right">
                <Button onClick={SettingsActions.handleUpdateSettings}>
                    {i18n.translate({ id: 'save', sourceName: 'Main' })}
                </Button>
            </div>
        </div>
    );
}

export default GeneralSettings;
